import { OpenInNew } from '@mui/icons-material';
import { useCurrentUser, useApplicationState, useApi } from 'lib/contexts/ApplicationState';
import { List } from 'lib/types';
import { useUserLists } from 'lib/contexts/userListsContext';
import { userIsPaidTeamMember } from 'lib/helpers';
import { A } from 'components/A';
import { WORK_FOR_US_NAME } from 'lib/constants/teams';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { useSignUpModal } from 'components/SignUpModal';
import { CreatorStageFilter } from 'components/Filters/CreatorStageFilter';
import { CreatorStage } from 'lib/types/teams';
import { useQuery } from '@tanstack/react-query';

interface Props {
  publicListId: string;
  filteredStages: number[];
}

export const ListInfo = ({ publicListId, filteredStages }: Props) => {
  const { lists } = useUserLists();
  const router = useRouter();
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const { isAuthed } = useApplicationState();
  const currentUser = useCurrentUser();
  const { showSignUpModal } = useSignUpModal();

  // Find the list in the user's lists (for authenticated users)
  const listFromUserLists = lists?.find((l) => l.publicId === publicListId);

  // Fetch public list for anonymous users or when list is not in user's lists
  const shouldFetchPublicList = isAuthed && lists !== undefined && !listFromUserLists;

  const { data: publicList } = useQuery<List>({
    queryKey: ['getPublicList', publicListId],
    queryFn: () => API.getPublicList(publicListId),
    enabled: shouldFetchPublicList,
    staleTime: 0,
  });

  // Use list from user's lists if available, otherwise use the fetched public list
  const list = listFromUserLists || publicList;

  const currentUserIsOnList = list?.listUsers?.find(
    (u) => u.userId === currentUser?.id && u.source === 'creator',
  );

  const updateList = async () => {
    if (!list?.id) {
      return;
    }

    if (!currentUser) {
      showSignUpModal('Please sign up or sign in to join this list!');
      return;
    }

    try {
      if (currentUserIsOnList) {
        await API.removeUserFromList(list?.id, currentUser?.id);
        enqueueSnackbar('You have been removed from the list!', { variant: 'success' });
      } else {
        await API.addUserToList(list?.id, currentUser?.id, 'creator');
        enqueueSnackbar('You have been added to the list!', { variant: 'success' });
      }
      router.reload();
    } catch (err) {
      enqueueSnackbar(`There was an error updating the list: ${err}`, { variant: 'error' });
    }
  };

  // Query for fetching creator stages
  const {
    data: creatorStages,
  } = useQuery<CreatorStage[]>({
    queryKey: ['getCreatorStages', list?.teamId],
    queryFn: () => API.getCreatorStages(list?.teamId!),
    enabled: !!list?.teamId,
    staleTime: 0, // Always refetch on open
  });

  if (list?.allowCreatorAdd) {
    const action = currentUserIsOnList ? 'Leave list' : 'Join list';
    return (
      <>
        <button type="button" className="btn-primary-outlined mb-2" onClick={updateList}>
          {action}
        </button>
      </>
    );
  }

  /* If a user is on a team, but this is *not* a team list then don't show collabs.
   * Otherwise they click to share it but they get a modal with no possible members
   * to select. However, non-team users will see this on every list and it will lead
   * to an upsell modal.
   *
   * TODO: In the future, maybe we do show this and when the user clicks it, they can
   * choose to copy to a team list.
   */
  if (userIsPaidTeamMember(currentUser) && !list?.teamId) {
    return null;
  }

  if (!isAuthed) {
    return null;
  }

  // Don't show collaborators on public lists
  if (currentUser && !list?.teamId && !list?.isPrivate) {
    return null;
  }

  if (!list?.isPrivate && !currentUser) {
    return (
      <div className="text-center text-xl text-orange mb-4">
        <button type="button" className="underline font-semibold" onClick={() => router.push({ pathname: '/signin', query: { returnTo: router.asPath } })}>
          Sign up or log in
        </button>
        {' '}
        to see all the creators on this list
      </div>
    );
  }

  return (
    <div className="flex flex-wrap m-auto justify-between">
      <div>
        {list?.jobId && list?.name !== WORK_FOR_US_NAME && (
          <A href={`/jobs/${list?.jobId}`}>
            View job associated with the list
            <span className="ml-2">
              <OpenInNew fontSize="small" />
            </span>
          </A>
        )}
      </div>
      {/* For filtering based on creator stage */}
      {(list?.teamId && userIsPaidTeamMember(currentUser) && !!creatorStages) && (
        <div className="md:w-1/4">
          <CreatorStageFilter creatorStages={creatorStages} selectedStages={filteredStages} />
        </div>
      )}
    </div>
  );
};
